/**
 * 売上明細 手入力画面。
 *
 * 設計方針 (docs/design/v02-manual-entry-requirements.md §2-3「案A」準拠):
 *   - 主要3種 (labor/opex/delivery_sales) と異なり、sales は「店舗×月」の全置換だと
 *     月内の他メニューの売上まで巻き込んで削除してしまう (CLAUDE.md §8-9)。
 *   - このため保存は `POST /imports/data/sales?mode=upsert&entry_scope=narrow` を使い、
 *     「店舗×月×メニュー」単位でのみ置換する。置換キーは送信された行から導出されるため、
 *     **入力のあったメニューだけが置き換わり、触っていないメニューには影響しない**。
 *
 * 2026-08-17 全メニュー一覧化 (実運用フィードバック):
 *   - 従来はメニューを1つ選ばないと入力欄が出ず、40品あれば40回選び直す必要があった。
 *     店舗×月を選んだ時点で「その月に有効な全メニュー」を行として並べ、まとめて入力できるようにした。
 *   - ⚠️ 「既存データのあるメニューを空欄にして削除する」は narrow 置換では表現できない
 *     (行が無い = 置換キーが作られない = 無反応。§8-71 ② と同型の罠)。
 *     このため空欄化による暗黙の削除は行わず、明示的な「この月のデータを削除」操作を用意し、
 *     行単位削除API (`POST /data/records/bulk-delete`) で消す。
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2, AlertTriangle, Info, ClipboardList, Search } from 'lucide-react';
import { apiFetch, ApiClientError, API_BASE, handleUnauthorized } from '@/lib/api';
import { buildCsvText } from '@/lib/csv-export';
import { toast } from '@/stores/toast';
import { ConfirmDialog } from '@/components/Modal';
import { MonthPicker } from '@/components/MonthPicker';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { InputReadyButton } from '@/components/publish/InputReadyButton';
import { useAuthStore, hasPermission } from '@/stores/auth';

type Store = { store_id: string; store_code: string; store_name: string; status: 'active' | 'closed' };
type Menu = { menu_id: string; store_id: string; menu_code: string; menu_name: string; effective_from: string; effective_to: string | null };
type RecordsResponse = { count: number; total_count: number; rows: Array<Record<string, unknown>> };
type ImportError = { row: number; code?: string; column?: string; message: string };
type PreviewResponse = { import_job_id: string; invalid_rows: number; errors: ImportError[] };
type CommitResponse = { inserted: number; error_count: number; errors: ImportError[]; status: string };

/** 画面上の1行。同一 menu_code が複数行あってよい (月内でメニュー改定があった場合の期間分割) */
type SalesRow = {
  menu_code: string;
  period_start: string;
  period_end: string;
  quantity: string;
  sales_amount: string;
};

const currentYearMonth = (): string => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

const lastDayOfMonth = (yearMonth: string): string => {
  const [y, m] = yearMonth.split('-').map(Number);
  return `${yearMonth}-${String(new Date(y!, m!, 0).getDate()).padStart(2, '0')}`;
};

/** GET /data/records は日付列を表示用に YYYY/MM/DD 形式で返す (data-browse.ts の toSlash)。
 *  <input type="date"> は YYYY-MM-DD 以外を受け付けず値が空欄表示になるため、スラッシュ→ハイフンに戻す */
const toIsoDate = (v: unknown): string => (v ? String(v).replace(/\//g, '-') : '');

const blankRow = (menuCode: string, month: string): SalesRow => ({
  menu_code: menuCode,
  period_start: `${month}-01`,
  period_end: lastDayOfMonth(month),
  quantity: '',
  sales_amount: '',
});

export function SalesEntryPage() {
  const qc = useQueryClient();

  const [storeId, setStoreId] = useState('');
  const [month, setMonth] = useState(currentYearMonth());
  const [rows, setRows] = useState<SalesRow[]>([]);
  const [menuFilter, setMenuFilter] = useState('');
  const [errors, setErrors] = useState<ImportError[] | null>(null);
  const [saving, setSaving] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [clearTarget, setClearTarget] = useState<{ menuCode: string; menuName: string; recordIds: string[] } | null>(null);
  /** 直近に「既存データから読み込んだ」store+month の組 (切替直後の誤上書き防止) */
  const loadedKeyRef = useRef<string | null>(null);

  const perms = useAuthStore((s) => s.user?.effective_permissions);
  const canDelete = ['manual_entry_sales', 'import'].some((f) => hasPermission(perms, f, 'delete'));

  const { data: stores = [] } = useQuery<Store[]>({
    queryKey: ['stores'],
    queryFn: () => apiFetch<Store[]>('/stores'),
  });

  const { data: menusRaw = [] } = useQuery<Menu[]>({
    queryKey: ['menus', storeId],
    queryFn: () => apiFetch<Menu[]>(`/menus?store_id=${storeId}`),
    enabled: Boolean(storeId),
  });

  /** menu_code 単位で重複排除し、最新 (effective_from が最大) のバージョンの名称を採用。
   *  ⚠️ 対象月に有効なバージョンが1つも無いメニューは除外する
   *  (廃止済みメニューを毎月並べると一覧が使い物にならなくなるため)。
   *  ただし既存データを持つメニューは、廃止済みでも下で必ず行に含める。 */
  const menuOptions = useMemo(() => {
    const monthStart = `${month}-01`;
    const monthEnd = lastDayOfMonth(month);
    const byCode = new Map<string, Menu>();
    for (const m of menusRaw) {
      const activeInMonth = m.effective_from <= monthEnd && (!m.effective_to || m.effective_to >= monthStart);
      if (!activeInMonth) continue;
      const existing = byCode.get(m.menu_code);
      if (!existing || m.effective_from > existing.effective_from) byCode.set(m.menu_code, m);
    }
    return [...byCode.values()].sort((a, b) => a.menu_code.localeCompare(b.menu_code));
  }, [menusRaw, month]);

  /** 全メニュー分をまとめて扱うため limit を引き上げる (既定 100 だと 40品×期間分割で溢れる) */
  const existingQuery = useQuery<RecordsResponse>({
    queryKey: ['data-records', 'sales', storeId, month],
    queryFn: () => {
      const params = new URLSearchParams({ data_type: 'sales', store_id: storeId, month, limit: '500' });
      return apiFetch<RecordsResponse>(`/data/records?${params}`);
    },
    enabled: Boolean(storeId && month),
  });

  /** menu_code → 既存行の record_id 一覧。空欄化による暗黙削除を避け、明示削除に使う */
  const existingIdsByMenu = useMemo(() => {
    const map = new Map<string, string[]>();
    for (const r of existingQuery.data?.rows ?? []) {
      const code = String(r.menu_code ?? '');
      if (!code) continue;
      const list = map.get(code) ?? [];
      list.push(String(r.record_id));
      map.set(code, list);
    }
    return map;
  }, [existingQuery.data]);

  const menuNameOf = (code: string) =>
    menuOptions.find((m) => m.menu_code === code)?.menu_name
    ?? String(existingQuery.data?.rows.find((r) => r.menu_code === code)?.menu_name ?? code);

  // 店舗×月が変わって既存データが読み込まれたら、全メニュー分の行を組み立て直す
  useEffect(() => {
    if (!storeId || !month || !existingQuery.data) return;
    const key = `${storeId}::${month}`;
    if (loadedKeyRef.current === key) return;
    loadedKeyRef.current = key;

    const existing = existingQuery.data.rows;
    const built: SalesRow[] = [];
    // 対象月に有効なメニューを常に1行以上並べる (空欄なら保存対象外)
    for (const m of menuOptions) {
      const mine = existing.filter((r) => r.menu_code === m.menu_code);
      if (mine.length === 0) { built.push(blankRow(m.menu_code, month)); continue; }
      for (const r of mine) {
        built.push({
          menu_code: m.menu_code,
          period_start: toIsoDate(r.period_start),
          period_end: toIsoDate(r.period_end),
          quantity: r.quantity != null ? String(r.quantity) : '',
          sales_amount: r.sales_amount != null ? String(r.sales_amount) : '',
        });
      }
    }
    // 対象月に有効でないのにデータを持つメニュー (廃止済み等) も、消せるように必ず出す
    const shown = new Set(menuOptions.map((m) => m.menu_code));
    for (const r of existing) {
      const code = String(r.menu_code ?? '');
      if (!code || shown.has(code)) continue;
      built.push({
        menu_code: code,
        period_start: toIsoDate(r.period_start),
        period_end: toIsoDate(r.period_end),
        quantity: r.quantity != null ? String(r.quantity) : '',
        sales_amount: r.sales_amount != null ? String(r.sales_amount) : '',
      });
    }
    setRows(built);
    setErrors(null);
  }, [storeId, month, existingQuery.data, menuOptions]);

  useEffect(() => {
    if (!storeId || !month) { setRows([]); loadedKeyRef.current = null; }
  }, [storeId, month]);

  const selectedStore = stores.find((s) => s.store_id === storeId);

  const updateCell = (idx: number, field: keyof SalesRow, value: string) => {
    setRows((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx]!, [field]: value };
      return next;
    });
  };

  /** 同一メニューの期間分割行を直下に追加する (月内でメニュー改定があった場合) */
  const splitPeriod = (idx: number) => {
    setRows((prev) => {
      const src = prev[idx]!;
      const next = [...prev];
      next.splice(idx + 1, 0, blankRow(src.menu_code, month));
      return next;
    });
  };

  const removeRow = (idx: number) => setRows((prev) => prev.filter((_, i) => i !== idx));

  const isRowFilled = (r: SalesRow) => r.quantity.trim() !== '' && r.sales_amount.trim() !== '';
  const filledRows = rows.filter(isRowFilled);
  const filledMenus = new Set(filledRows.map((r) => r.menu_code));

  const dataReady = Boolean(storeId && month)
    && loadedKeyRef.current === `${storeId}::${month}`
    && !existingQuery.isFetching;

  /** 表示上の絞り込み (保存対象には影響しない) */
  const visibleRows = useMemo(() => {
    const kw = menuFilter.trim().toLowerCase();
    return rows
      .map((r, idx) => ({ r, idx }))
      .filter(({ r }) => !kw
        || r.menu_code.toLowerCase().includes(kw)
        || menuNameOf(r.menu_code).toLowerCase().includes(kw));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, menuFilter, menuOptions, existingQuery.data]);

  const openConfirm = () => {
    if (!storeId || !selectedStore) { toast.error('店舗を選択してください'); return; }
    if (!month) { toast.error('対象月を選択してください'); return; }
    if (!dataReady) { toast.error('既存データの読込が完了していません。少し待ってから再度お試しください'); return; }
    if (filledRows.length === 0) { toast.error('入力内容がありません'); return; }
    setConfirmOpen(true);
  };

  const doSave = async () => {
    if (!storeId || !selectedStore || !month) return;

    const headers = ['store_code', 'menu_code', 'sales_month', 'period_start', 'period_end', 'quantity', 'sales_amount'];
    const csvRows = filledRows.map((row) => ({
      store_code: selectedStore.store_code,
      menu_code: row.menu_code,
      sales_month: `${month}-01`,
      period_start: row.period_start,
      period_end: row.period_end,
      quantity: row.quantity,
      sales_amount: row.sales_amount,
    }));
    // escapeFormulas=false: ブラウザ生成CSVを自前パーサへ直接アップロードする往復専用データのため
    const csvText = buildCsvText(headers, csvRows, false);
    const file = new File([csvText], 'manual-sales.csv', { type: 'text/csv' });

    setSaving(true);
    setErrors(null);
    setConfirmOpen(false);
    try {
      const form = new FormData();
      form.append('file', file);
      const token = localStorage.getItem('access_token');
      const previewRes = await fetch(`${API_BASE}/imports/data/sales?mode=upsert&entry_scope=narrow`, {
        method: 'POST',
        body: form,
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      handleUnauthorized(previewRes.status);
      const previewBody = await previewRes.json();
      if (!previewRes.ok || !previewBody.success) {
        throw new ApiClientError(previewRes.status, previewBody.error?.code ?? 'UNKNOWN', previewBody.error?.message ?? '検証に失敗しました');
      }
      const preview = previewBody.data as PreviewResponse;
      if (preview.invalid_rows > 0) {
        setErrors(preview.errors);
        toast.error(`入力内容にエラーがあります (${preview.invalid_rows} 件)`);
        return;
      }

      const commit = await apiFetch<CommitResponse>(`/imports/${preview.import_job_id}/commit`, { method: 'POST' });
      if (commit.error_count > 0) {
        setErrors(commit.errors);
        toast.error(`保存中にエラーが発生しました (${commit.error_count} 件)`);
        return;
      }

      toast.success(`保存しました（${filledMenus.size} メニュー / ${commit.inserted} 件）`);
      loadedKeyRef.current = null;
      qc.invalidateQueries({ queryKey: ['data-records'] });
      qc.invalidateQueries({ queryKey: ['data-summary'] });
    } catch (e) {
      toast.error(e instanceof ApiClientError ? e.message : '保存に失敗しました');
    } finally {
      setSaving(false);
    }
  };

  /** 明示的な削除。narrow 置換では「行が無い」を表現できないため行単位削除APIを使う */
  const doClearMenu = async () => {
    if (!clearTarget) return;
    setSaving(true);
    try {
      const res = await apiFetch<{ requested: number; deleted: number }>('/data/records/bulk-delete', {
        method: 'POST',
        body: JSON.stringify({ data_type: 'sales', record_ids: clearTarget.recordIds }),
      });
      toast.success(`${clearTarget.menuName} の ${month} のデータを削除しました（${res.deleted} 件）`);
      setClearTarget(null);
      loadedKeyRef.current = null;
      qc.invalidateQueries({ queryKey: ['data-records'] });
      qc.invalidateQueries({ queryKey: ['data-summary'] });
    } catch (e) {
      toast.error(e instanceof ApiClientError ? e.message : '削除に失敗しました');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6 max-w-6xl">
      <div>
        <h1 className="text-h1 flex items-center gap-2">
          <ClipboardList size={26} className="text-brand-red" strokeWidth={1.75} />
          売上明細 手入力
        </h1>
        <p className="text-sm text-neutral-sub mt-1">
          店舗・対象月を選択すると、その月に有効な全メニューが一覧で並びます。数量・売上金額を入力して保存してください。
          <strong className="font-bold">入力のあったメニューだけが置き換わり、空欄のままのメニューには影響しません</strong>（CSV取込の補完用途を想定しています）
        </p>
        {/* 設計 §5-3 の相互導線。期間横断の閲覧・検索・1行単位の修正は /data が担当する。 */}
        <p className="text-sm text-neutral-sub mt-2 flex items-center gap-1.5 flex-wrap">
          <Search size={14} aria-hidden className="shrink-0" />
          複数月をまたいで見渡したい場合は
          <Link to="/data?data_type=sales" className="font-bold text-chrome-base underline hover:no-underline">
            取込済みデータ
          </Link>
          をお使いください（期間指定・検索・行単位の修正ができます）
        </p>
      </div>

      {/* 絞り込み */}
      <div className="bg-white border border-surface-line rounded-lg p-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          <div className="block">
            <span className="block text-[12px] font-bold text-text-muted mb-1" id="sales-store-label">店舗 <span className="text-text-sub">*</span></span>
            <Select value={storeId} onValueChange={setStoreId}>
              <SelectTrigger className="w-full min-h-[44px]" aria-labelledby="sales-store-label"><SelectValue placeholder="選択してください" /></SelectTrigger>
              <SelectContent>
                {stores.filter((s) => s.status === 'active').map((s) => (
                  <SelectItem key={s.store_id} value={s.store_id}>{s.store_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <label className="block">
            <span className="block text-[12px] font-bold text-text-muted mb-1">対象月 <span className="text-text-sub">*</span></span>
            <MonthPicker
              aria-label="対象月 (必須)"
              value={month} onChange={setMonth}
              className="w-full min-h-[44px] px-3 rounded-md border border-surface-line text-[13px] focus-ring"
            />
          </label>
          <label className="block">
            <span className="block text-[12px] font-bold text-text-muted mb-1">メニューを絞り込む</span>
            <input
              type="search" value={menuFilter} onChange={(e) => setMenuFilter(e.target.value)}
              placeholder="メニュー名・コードで絞り込み"
              className="w-full min-h-[44px] px-3 rounded-md border border-surface-line text-[13px] focus-ring"
            />
          </label>
        </div>
        {storeId && month && (
          <p className="text-[13px] text-text-muted mt-3 flex items-center gap-1.5">
            <Info size={14} aria-hidden className="shrink-0" />
            {existingQuery.isFetching
              ? '既存データを読み込んでいます…'
              : `${menuOptions.length} メニュー中 ${existingIdsByMenu.size} メニューに ${month} のデータがあります`}
          </p>
        )}
      </div>

      {errors && errors.length > 0 && (
        <div className="rounded-lg border border-danger-border bg-danger-bg p-4">
          <p className="text-sm font-bold text-danger-text flex items-center gap-1.5">
            <AlertTriangle size={16} aria-hidden /> 入力エラー
          </p>
          <ul className="mt-2 space-y-1 text-[13px] text-danger-text list-disc pl-5">
            {errors.slice(0, 20).map((e, i) => (
              <li key={i}>{e.row} 行目{e.column ? `（${e.column}）` : ''}: {e.message}</li>
            ))}
          </ul>
        </div>
      )}

      {storeId && month && (
        <div className="bg-white border border-surface-line rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead className="bg-surface-sunken text-text-muted">
                <tr>
                  <th scope="col" className="relative text-left px-3 py-2 font-bold min-w-[180px]">メニュー</th>
                  <th scope="col" className="relative text-left px-3 py-2 font-bold">期間開始 <span className="text-text-sub">*</span></th>
                  <th scope="col" className="relative text-left px-3 py-2 font-bold">期間終了 <span className="text-text-sub">*</span></th>
                  <th scope="col" className="relative text-left px-3 py-2 font-bold">数量</th>
                  <th scope="col" className="relative text-left px-3 py-2 font-bold">売上金額（税込、円）</th>
                  <th scope="col" className="relative text-right px-3 py-2 font-bold w-[120px]">操作</th>
                </tr>
              </thead>
              <tbody>
                {visibleRows.length === 0 && (
                  <tr>
                    <td colSpan={6} className="text-center text-text-muted py-8">
                      {existingQuery.isFetching ? '読み込み中…' : 'このメニュー名に一致する行がありません'}
                    </td>
                  </tr>
                )}
                {visibleRows.map(({ r, idx }) => {
                  const hasExisting = (existingIdsByMenu.get(r.menu_code)?.length ?? 0) > 0;
                  const sameMenuAbove = idx > 0 && rows[idx - 1]!.menu_code === r.menu_code;
                  return (
                    <tr key={`${r.menu_code}-${idx}`} className="border-t border-surface-line align-middle">
                      <td className="px-3 py-2">
                        {sameMenuAbove ? (
                          <span className="text-text-muted text-[12px]">↳ 同じメニューの別期間</span>
                        ) : (
                          <span className="font-bold">
                            {r.menu_code} — {menuNameOf(r.menu_code)}
                            {hasExisting && (
                              <span className="ml-1.5 text-[11px] font-normal text-text-sub">（登録済み）</span>
                            )}
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        <input type="date" value={r.period_start} onChange={(e) => updateCell(idx, 'period_start', e.target.value)}
                          aria-label={`${menuNameOf(r.menu_code)} の期間開始`}
                          className="min-h-[44px] px-2 rounded-md border border-surface-line text-[13px] focus-ring" />
                      </td>
                      <td className="px-3 py-2">
                        <input type="date" value={r.period_end} onChange={(e) => updateCell(idx, 'period_end', e.target.value)}
                          aria-label={`${menuNameOf(r.menu_code)} の期間終了`}
                          className="min-h-[44px] px-2 rounded-md border border-surface-line text-[13px] focus-ring" />
                      </td>
                      <td className="px-3 py-2">
                        <input type="number" inputMode="numeric" value={r.quantity} onChange={(e) => updateCell(idx, 'quantity', e.target.value)}
                          aria-label={`${menuNameOf(r.menu_code)} の数量`} placeholder="—"
                          className="w-28 min-h-[44px] px-2 rounded-md border border-surface-line text-[13px] focus-ring" />
                      </td>
                      <td className="px-3 py-2">
                        <input type="number" inputMode="numeric" value={r.sales_amount} onChange={(e) => updateCell(idx, 'sales_amount', e.target.value)}
                          aria-label={`${menuNameOf(r.menu_code)} の売上金額`} placeholder="—"
                          className="w-36 min-h-[44px] px-2 rounded-md border border-surface-line text-[13px] focus-ring" />
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex items-center justify-end gap-1">
                          <button type="button" onClick={() => splitPeriod(idx)}
                            aria-label={`${menuNameOf(r.menu_code)} の期間を分ける`} title="期間を分ける"
                            className="min-h-[44px] min-w-[44px] rounded-md border border-surface-line hover:bg-surface-sunken focus-ring flex items-center justify-center">
                            <Plus size={15} aria-hidden />
                          </button>
                          {sameMenuAbove ? (
                            <button type="button" onClick={() => removeRow(idx)}
                              aria-label={`${menuNameOf(r.menu_code)} のこの期間の行を削除`} title="この行を削除"
                              className="min-h-[44px] min-w-[44px] rounded-md border border-surface-line hover:bg-surface-sunken focus-ring flex items-center justify-center">
                              <Trash2 size={15} aria-hidden />
                            </button>
                          ) : hasExisting && canDelete ? (
                            <button type="button"
                              onClick={() => setClearTarget({
                                menuCode: r.menu_code,
                                menuName: menuNameOf(r.menu_code),
                                recordIds: existingIdsByMenu.get(r.menu_code) ?? [],
                              })}
                              aria-label={`${menuNameOf(r.menu_code)} の ${month} のデータを削除`} title="この月のデータを削除"
                              className="min-h-[44px] min-w-[44px] rounded-md border border-surface-line hover:bg-surface-sunken focus-ring flex items-center justify-center">
                              <Trash2 size={15} aria-hidden />
                            </button>
                          ) : <span className="inline-block min-w-[44px]" />}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="px-4 py-3 border-t border-surface-line bg-surface-sunken flex flex-wrap items-center justify-between gap-3">
            <p className="text-[13px] text-text-muted">
              入力済み: <strong className="tabular-nums">{filledMenus.size}</strong> メニュー / {filledRows.length} 行
              <span className="ml-2 text-text-sub">空欄のメニューは保存対象外です</span>
            </p>
            <div className="flex items-center gap-2">
              <InputReadyButton nodeId={storeId} targetMonth={month} dataType="sales" />
              <button type="button" onClick={openConfirm} disabled={saving || filledRows.length === 0}
                className="min-h-[44px] px-5 rounded-md bg-brand-red hover:bg-brand-red-hover text-white text-[13px] font-extrabold btn-press-primary disabled:opacity-45 disabled:cursor-not-allowed focus-ring">
                {saving ? '保存中…' : '保存する'}
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={confirmOpen}
        title={`${filledMenus.size} メニュー分を保存しますか？`}
        loading={saving}
        confirmLabel="保存する"
        message={(
          <div className="space-y-3 text-left text-[13px]">
            <p>
              {selectedStore?.store_name} / {month} の
              <strong>入力のあった {filledMenus.size} メニュー</strong>（{filledRows.length} 行）を保存します。
            </p>
            <p className="text-text-muted">
              入力したメニューの当該月データは置き換わります。空欄のままのメニューには影響しません。
            </p>
          </div>
        )}
        onConfirm={doSave}
        onClose={() => setConfirmOpen(false)}
      />

      <ConfirmDialog
        open={!!clearTarget}
        title="このメニューの当月データを削除しますか？"
        destructive
        loading={saving}
        confirmLabel="削除する"
        message={(
          <div className="space-y-3 text-left text-[13px]">
            <p>
              <strong>{clearTarget?.menuName}</strong> の {month} のデータ
              <strong>{clearTarget?.recordIds.length} 件</strong>を削除します。
            </p>
            <p>この操作は取り消せません。</p>
          </div>
        )}
        onConfirm={doClearMenu}
        onClose={() => setClearTarget(null)}
      />
    </div>
  );
}
